import moment from 'moment-timezone';

/**
 * Gets the closes interval minute to the date, ceiling the value
 *
 * @param date - The date to check
 * @param interval - The interval, e.g. 15 minutes = 0, 15, 30, 45
 * @returns The new date
 */
export function toNearestNextMinuteInterval(date: Date, interval = 15): Date {
    const original = moment(date);
    const remainder = interval - (original.minute() % interval);
    // Already in the interval
    if (remainder % interval === 0) {
        return date;
    }
    return original.add(remainder, 'minutes').toDate();
}

/**
 * Gets the closes interval minute to the date, flooring the value
 *
 * @param date - The date to check
 * @param interval - The interval, e.g. 15 minutes = 0, 15, 30, 45
 * @returns The new date
 */
export function toNearestPrevMinuteInterval(date: Date, interval = 15): Date {
    const original = moment(date);
    const remainder = interval - (original.minute() % interval);
    // Already in the interval
    if (remainder % interval === 0) {
        return date;
    }
    return original.subtract(interval - remainder, 'minutes').toDate();
}

export function isBeforeToday(date: Date): boolean {
    const today = getDateWithoutTime(new Date());
    return date.getTime() < today.getTime();
}

export function isToday(date: Date): boolean {
    const today = new Date();
    return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
    );
}

export const getUtcDateWithoutTime = (date: Date): Date =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));

export const getDateWithoutTime = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

export function groupByDay<T>(objects: T[], keyExtractor: (object: T) => number): { [key: number]: T[] } {
    const groupedObjects = objects.reduce<{ [key: number]: T[] }>((res, current) => {
        const dateKey = keyExtractor(current);
        if (dateKey in res) {
            res[dateKey] = [...res[dateKey], current];
        } else {
            res[dateKey] = [current];
        }
        return res;
    }, {});
    return groupedObjects;
}

export function getWeekInMonthText(date: Date, returnNumber?: boolean): string {
    // Based on: https://github.com/moment/moment/issues/2934#issuecomment-182541809
    const dayOfMonth = moment(date).date();

    // TODO: Replace with i18n
    if (dayOfMonth <= 7) {
        return returnNumber ? '1st' : 'first';
    } else if (dayOfMonth > 7 && dayOfMonth <= 14) {
        return returnNumber ? '2nd' : 'second';
    } else if (dayOfMonth > 14 && dayOfMonth <= 21) {
        return returnNumber ? '3rd' : 'third';
    } else if (dayOfMonth > 21 && dayOfMonth <= 28) {
        return returnNumber ? '4th' : 'fourth';
    } else {
        return returnNumber ? '5th' : 'fifth';
    }
}

export function toIso(date: Date): Date {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
}

/**
 * @deprecated Use the explicit startOfMonthInTz
 * @param date - The date
 * @returns The start of month in UTC? Maybe?
 */
export function startOfMonth(date: Date): Date {
    return toIso(new Date(date.getFullYear(), date.getMonth(), 1));
}

/**
 * @deprecated Use the explicit endOfMonthInTz
 * @param date - The date
 * @returns The end of month in UTC? Maybe?
 */
export function endOfMonth(date: Date): Date {
    return toIso(new Date(date.getFullYear(), date.getMonth() + 1, 0, 11, 59, 59));
}

export function startOfMonthInTz(date: Date, timezone?: string): Date {
    const momentDate = timezone ? moment.tz(date, timezone) : moment.utc(date);
    return momentDate.startOf('month').toDate();
}
export function endOfMonthInTz(date: Date, timezone?: string): Date {
    const momentDate = timezone ? moment.tz(date, timezone) : moment.utc(date);
    return momentDate.endOf('month').toDate();
}

export const addMonths = (date: Date, months: number): Date => {
    const temp = new Date(date.getTime());
    return new Date(temp.setMonth(temp.getMonth() + months));
};

// It is important to maintain the time units in decending order (largest time unit to smallest time unit).
const timeUnitArray = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] as const;
/**
 * @deprecated This type has been moved to the 'dates' package
 */
export type TimeUnit = typeof timeUnitArray[number];

/**
 * @deprecated This function has been moved to the 'dates' package
 * @param from - starting Date
 * @param to - ending Date
 * @returns - object containing the time difference and time unit
 */
export function getDifferenceWithGreatestUnitOfTime(from: Date, to: Date): { timeDifference: number; unit: TimeUnit } {
    const fromDate = moment(from);
    const toDate = moment(to);
    for (const timeUnit of timeUnitArray) {
        const timeDiff = toDate.diff(fromDate, timeUnit);
        if (timeDiff > 0) {
            return { timeDifference: timeDiff, unit: timeUnit };
        }
    }
    return { timeDifference: 0, unit: 'seconds' };
}

/**
 * @deprecated This function has been moved to the 'dates' package
 * @param timeUnit - unit of time to convert to string
 * @param plural - whether the unit string should be plural
 * @returns - time unit string
 */
export function timeUnitToString(timeUnit: TimeUnit, plural = true): string {
    return plural ? timeUnit : timeUnit.slice(0, timeUnit.length - 1);
}
